import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import {
  BuildingOffice2Icon,
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

import { client } from '~/api/client';
import {
  Action,
  ClientSortField,
  Resource,
  SortDirection,
  useSearchClientsQuery
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { usePermissions } from '~/components/permissions';

export type LinkedAccount = { id: string; name: string; summary: string };

// Above the dialog it opens inside, which sits at the scale's top (z-50). Same
// escape hatch, same reason, as VISIBILITY_POPOVER_PANEL_CLASS.
const PANEL_CLASS = clsx(
  'z-[100] w-80 rounded-lg bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none',
  'origin-bottom transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in',
  'dark:bg-neutral-900 dark:ring-white/10'
);

export const clientSummary = (input: {
  isCommercial: boolean;
  isPersonal: boolean;
  agencyUserOwner?: { name: string } | null;
}) =>
  [
    input.isCommercial ? 'Commercial' : input.isPersonal ? 'Personal' : null,
    input.agencyUserOwner?.name
  ]
    .filter(Boolean)
    .join(' · ');

/** Hidden until Clients is released — an empty picker on every run card looks broken. */
const AccountPicker = ({
  value,
  onChange
}: {
  value?: LinkedAccount;
  onChange: (next?: LinkedAccount) => void;
}) => {
  const [query, setQuery] = React.useState('');

  const { data, loading } = useSearchClientsQuery({
    client,
    variables: {
      first: 8,
      filter: query.trim() ? { nameQuery: query.trim() } : undefined,
      sort: { field: ClientSortField.UpdatedAt, direction: SortDirection.Desc }
    },
    fetchPolicy: 'cache-and-network'
  });

  const results = (data?.clients.edges ?? []).map((edge) => edge.node);

  return (
    <Headless.Popover className="relative">
      <Headless.PopoverButton
        className={clsx(
          'inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm/6 text-neutral-950 dark:text-white',
          'hover:bg-neutral-100 dark:hover:bg-white/10 data-[open]:bg-neutral-100 dark:data-[open]:bg-neutral-700'
        )}
      >
        <BuildingOffice2Icon className="size-4 text-neutral-500 dark:text-neutral-400" />
        <span className="max-w-48 truncate">{value ? value.name : 'No linked account'}</span>
        <ChevronDownIcon className="size-4 text-neutral-500 dark:text-neutral-400" />
      </Headless.PopoverButton>
      <Headless.PopoverPanel
        transition
        anchor={{ to: 'top start', gap: 8 }}
        className={PANEL_CLASS}
      >
        {({ close }) => (
          <>
            {/* Bare, like the template list: the panel already draws the border
                and the focus ring. */}
            <div className="flex items-center gap-2 border-b border-neutral-950/10 dark:border-white/10 px-2">
              <MagnifyingGlassIcon className="size-4 shrink-0 text-neutral-500 dark:text-neutral-400" />
              <Headless.Input
                name="account-search"
                aria-label="Search accounts"
                placeholder="Search accounts"
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
                className="w-full border-0 bg-transparent py-2 text-sm/6 text-neutral-950 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-0"
              />
            </div>
            <div className="max-h-64 overflow-y-auto pt-1">
              {loading && results.length === 0 && (
                <p className="px-2 py-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                  Searching…
                </p>
              )}
              {!loading && results.length === 0 && (
                <p className="px-2 py-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                  {query.trim() ? `No accounts match “${query}”.` : 'No accounts yet.'}
                </p>
              )}
              {results.map((account) => {
                const selected = value?.id === account.id;
                return (
                  <Headless.Button
                    key={account.id}
                    type="button"
                    onClick={() => {
                      onChange({
                        id: account.id,
                        name: account.name,
                        summary: clientSummary(account)
                      });
                      close();
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left',
                      selected
                        ? 'bg-primary-50 dark:bg-primary-500/10'
                        : 'hover:bg-neutral-50 dark:hover:bg-white/5'
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span
                        className={clsx(
                          'block truncate text-sm',
                          selected
                            ? 'text-primary-900 dark:text-primary-200'
                            : 'text-neutral-950 dark:text-white'
                        )}
                      >
                        {account.name}
                      </span>
                      <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
                        {clientSummary(account)}
                      </span>
                    </span>
                    <CheckIcon
                      className={clsx(
                        'size-4 shrink-0',
                        selected ? 'text-primary-600 dark:text-primary-400' : 'text-transparent'
                      )}
                    />
                  </Headless.Button>
                );
              })}
            </div>
            {value && (
              <div className="mt-1 border-t border-neutral-950/10 dark:border-white/10 pt-1">
                <Headless.Button
                  type="button"
                  onClick={() => {
                    onChange(undefined);
                    close();
                  }}
                  className="flex w-full rounded-lg px-2 py-1.5 text-left text-sm text-neutral-950 dark:text-white hover:bg-neutral-50 dark:hover:bg-white/5"
                >
                  No linked account
                </Headless.Button>
              </div>
            )}
          </>
        )}
      </Headless.PopoverPanel>
    </Headless.Popover>
  );
};

export const RunCardFooterControls = ({
  account,
  onChangeAccount
}: {
  account?: LinkedAccount;
  onChangeAccount: (next?: LinkedAccount) => void;
}) => {
  const clientsReleased = useFlag(FEATURE_FLAGS.SHOW_CLIENTS_TAB);
  const { hasPermission } = usePermissions();
  const canPickAccount = clientsReleased && hasPermission(Resource.Client, Action.Update);

  return (
    <div className="flex items-center justify-center gap-1">
      {canPickAccount && <AccountPicker value={account} onChange={onChangeAccount} />}
    </div>
  );
};
